// ============================================================
// LanguageSwitcher.jsx
// Kompaktes Dropdown im Header — zeigt aktuelle Sprache,
// Klick öffnet Auswahl aller verfügbaren Sprachen.
// ============================================================

import { useState, useEffect, useRef } from "react";
import { useLanguage } from "./LanguageContext";

const styles = `
  .lang-switcher {
    position: relative;
  }

  .lang-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    border-radius: 6px;
    border: 1.5px solid #e2e8f0;
    background: transparent;
    color: #0f172a;
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    height: 32px;
  }

  .lang-btn:hover {
    border-color: #94a3b8;
    background: #f8fafc;
  }

  .lang-btn-chevron {
    font-size: 9px;
    color: #94a3b8;
    transition: transform 0.2s;
  }

  .lang-btn.open .lang-btn-chevron {
    transform: rotate(180deg);
  }

  .lang-globe {
    width: 13px;
    height: 13px;
    color: #64748b;
  }

  .lang-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    width: 148px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
    overflow: hidden;
    z-index: 300;
    animation: langDropIn 0.16s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes langDropIn {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .lang-dropdown-label {
    padding: 9px 12px 6px;
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    border-bottom: 1px solid #f1f5f9;
  }

  .lang-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 12px;
    cursor: pointer;
    transition: background 0.12s;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    font-family: 'new-science', 'DM Sans', sans-serif;
  }

  .lang-option:hover {
    background: #f8fafc;
  }

  .lang-option.active {
    background: #eff6ff;
  }

  .lang-option-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .lang-flag {
    font-size: 15px;
    line-height: 1;
  }

  .lang-option-name {
    font-size: 13px;
    color: #0f172a;
    font-weight: 500;
  }

  .lang-option.active .lang-option-name {
    color: #2563eb;
    font-weight: 600;
  }

  .lang-checkmark {
    font-size: 11px;
    color: #2563eb;
  }

  .lang-option-code {
    font-size: 10px;
    color: #94a3b8;
    font-weight: 600;
    letter-spacing: 0.04em;
  }
`;

// Flaggen-Emojis pro Sprachcode
const FLAGS = {
  de: "🇩🇪",
  en: "🇬🇧",
  fr: "🇫🇷",
  it: "🇮🇹",
};

const LANG_NAMES = {
  de: "Deutsch",
  en: "English",
  fr: "Français",
  it: "Italiano",
};

export default function LanguageSwitcher() {
  const { lang, setLang, LANGUAGES } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Schliessen bei Klick ausserhalb
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleSelect = (code) => {
    setLang(code);
    setOpen(false);
  };

  return (
    <div className="lang-switcher" ref={ref}>
      <style>{styles}</style>

      <button
        className={`lang-btn ${open ? "open" : ""}`}
        onClick={() => setOpen(o => !o)}
        title="Sprache wechseln / Change language"
      >
        {/* Globe icon */}
        <svg className="lang-globe" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        {lang.toUpperCase()}
        <span className="lang-btn-chevron">▾</span>
      </button>

      {open && (
        <div className="lang-dropdown">
          <div className="lang-dropdown-label">Sprache / Language</div>
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              className={`lang-option ${lang === l.code ? "active" : ""}`}
              onClick={() => handleSelect(l.code)}
            >
              <div className="lang-option-left">
                <span className="lang-flag">{FLAGS[l.code]}</span>
                <span className="lang-option-name">{LANG_NAMES[l.code]}</span>
              </div>
              {lang === l.code
                ? <span className="lang-checkmark">✓</span>
                : <span className="lang-option-code">{l.code.toUpperCase()}</span>
              }
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
